import RequestService from '~/services/requestsService/requests.service';
import {
  UsersEntity,
  UsersEntityInsert,
} from '@bitecritic-api/src/users/users.zod';
import {
  UserDetailResponseDto,
  UserResponseDto,
  UserPrivateProfileResponseDto,
} from '@bitecritic-api/src/users/dto/user.response.dto';

const BASE_USER = '/users';

const getProfile = () => {
  return RequestService.fetch<UserPrivateProfileResponseDto>(
    `${BASE_USER}/profile`,
  );
};

const updateProfile = async (body: Partial<UsersEntityInsert>) => {
  const response = await RequestService.patch<{
    user: UserPrivateProfileResponseDto;
    token: string;
  }>(`${BASE_USER}/profile`, body);
  return response;
};

const getPublicProfile = (username: string) => {
  return RequestService.fetch<UserResponseDto>(`${BASE_USER}/${username}`);
};

const followUser = (userId: string) => {
  return RequestService.post(`${BASE_USER}/${userId}/follow`);
};

const unfollowUser = async (userId: string) => {
  await RequestService.delete(`${BASE_USER}/${userId}/follow`);
  return {
    followingId: userId,
  };
};

const checkUsername = (username: string) => {
  return RequestService.fetch<{ available: boolean }>(
    `${BASE_USER}/username/${username}/check`,
  );
};

const updateLocale = async (data: {
  countryCode: string;
  languageCode: string;
}) => {
  const response = await RequestService.patch<UsersEntity>(
    `${BASE_USER}/locale`,
    data,
  );
  return response;
};

const getUserDetail = (username: string, language: string) => {
  return RequestService.fetch<UserDetailResponseDto>(
    `${BASE_USER}/${username}/detail?language=${language}`,
  );
};

export interface UsersSearchParams {
  searchQuery?: string;
  page?: number;
  pageSize?: number;
  countryId?: number;
}

export interface UsersSearchResponse {
  users: UserResponseDto[];
  total: number;
}

export const searchUsers = async (
  params: UsersSearchParams,
): Promise<UsersSearchResponse | null> => {
  const { searchQuery, page = 1, pageSize = 9, countryId } = params;

  return RequestService.fetch<UsersSearchResponse>(`${BASE_USER}/search`, {
    method: 'GET',
    params: {
      query: searchQuery,
      page,
      pageSize,
      countryId,
    },
  });
};

export const searchUsersByQuery = async (
  query: string,
): Promise<UserResponseDto[] | null> => {
  const response = await searchUsers({ searchQuery: query });
  return response?.users || [];
};

const deleteAccount = async (): Promise<void> => {
  await RequestService.delete(`${BASE_USER}/profile`);
};

const usersService = {
  getProfile,
  updateProfile,
  getPublicProfile,
  followUser,
  unfollowUser,
  checkUsername,
  updateLocale,
  getUserDetail,
  searchUsers,
  searchUsersByQuery,
  deleteAccount,
};

export default usersService;
